import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { money, when, pretty } from "./claimsShared";

const BUCKETS = ["0-30", "31-60", "61-90", "90+"];

export default function RecoveriesPage() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some((r) => ["Admin", "admin"].includes(r));
  const [data, setData] = useState({ recoveries: [], aging: {}, open_total: 0 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/claims/recoveries").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load recoveries"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setDialog(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "That did not work"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="recoveries-page">
      <h1 className="text-xl font-bold text-zinc-900">Money to Get Back</h1>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="recoveries-aging">
        {BUCKETS.map((b) => (
          <div key={b} className="bg-white border border-zinc-200 rounded-lg p-3" data-testid={`aging-bucket-${b}`}>
            <p className="text-[11px] text-zinc-500">{b} days</p>
            <p className="text-base font-bold text-zinc-900">{money(data.aging?.[b] || 0)}</p>
          </div>
        ))}
        <div className="bg-zinc-900 text-white rounded-lg p-3" data-testid="aging-open-total">
          <p className="text-[11px] text-zinc-300">Open total</p>
          <p className="text-base font-bold">{money(data.open_total || 0)}</p>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>{["Recovery", "Party", "Type", "Claim", "Customer", "Amount", "Aging", "Status", "Reference", "Raised", ""].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && data.recoveries.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-400" data-testid="recoveries-empty">No recoveries yet.</td></tr>)}
            {data.recoveries.map((r) => (
              <tr key={r.recovery_id} className="border-t border-zinc-100" data-testid={`recovery-row-${r.recovery_id}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.recovery_id}</td>
                <td className="px-3 py-2">{r.party_name || "—"}</td>
                <td className="px-3 py-2">{pretty(r.party_type)}</td>
                <td className="px-3 py-2 font-mono">{r.claim_id}</td>
                <td className="px-3 py-2 text-zinc-500">{r.customer_name}</td>
                <td className="px-3 py-2 font-medium">{money(r.amount)}</td>
                <td className="px-3 py-2">
                  <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-semibold" data-testid={`recovery-aging-${r.recovery_id}`}>
                    {r.aging_bucket} days
                  </span>
                </td>
                <td className="px-3 py-2" data-testid={`recovery-status-${r.recovery_id}`}>{pretty(r.status)}</td>
                <td className="px-3 py-2 text-zinc-500">{r.recovered_ref || r.written_off_reason || "—"}</td>
                <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{when(r.created_at)}</td>
                <td className="px-3 py-2 space-x-1 whitespace-nowrap">
                  {r.status === "open" && (
                    <>
                      <Button size="sm" variant="outline" className="h-6 text-[11px]"
                        onClick={() => { setForm({ recovery_id: r.recovery_id }); setDialog("recovered"); }}
                        data-testid={`mark-recovered-${r.recovery_id}`}>Mark recovered</Button>
                      {isAdmin && (
                        <Button size="sm" variant="outline" className="h-6 text-[11px]"
                          onClick={() => { setForm({ recovery_id: r.recovery_id }); setDialog("writeoff"); }}
                          data-testid={`write-off-${r.recovery_id}`}>Write off</Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-md" data-testid={`recovery-dialog-${dialog}`}>
          <DialogHeader>
            <DialogTitle className="text-base">
              {dialog === "recovered" ? "Mark as recovered" : "Write off this recovery"}
            </DialogTitle>
          </DialogHeader>
          {dialog === "recovered" ? (
            <div>
              <Label className="text-xs">Payment / adjustment reference</Label>
              <Input className="h-8 text-xs" value={form.reference || ""}
                onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} data-testid="recovery-reference-input" />
            </div>
          ) : (
            <div>
              <Label className="text-xs">Reason (at least 10 characters)</Label>
              <Textarea rows={3} className="text-xs" value={form.reason || ""}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="recovery-writeoff-reason" />
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setDialog(null)} data-testid="recovery-dialog-cancel">Cancel</Button>
            <Button size="sm" disabled={busy} data-testid="recovery-dialog-confirm" onClick={() => {
              if (dialog === "recovered") return act(() => api.post(`/claims/recoveries/${form.recovery_id}/recovered`, { reference: form.reference }), "Marked recovered");
              return act(() => api.post(`/claims/recoveries/${form.recovery_id}/write-off`, { reason: form.reason }), "Written off");
            }}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
